import './FeedEditSchedule.css'
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type SetStateAction,
} from 'react'
import {
  type FeedCull,
  type FeedSchedule,
  FeedKeepFromDay,
  feedValidateScheduleSpec,
} from 'aevocam-rest-api-spec'
import { SelectBox, type SelectBoxOption } from '@/components/scalar/SelectBox'
import { Button } from '@/components/action/Button'
import { IconButton } from '@/components/action/IconButton'
import { Notice } from '@/components/action/Notice'
import { toastWarning } from '@/components/action/Toaster'
import { useApi } from '@/hooks/useApi'
import { useTenantId } from '@/hooks/useTenantId'
import { TimeSlots } from './TimeSlots'
import { strToTimeSlots, timeSlotsToStr } from './FeedEditPage_helpers'
import { Switch } from '@/components/scalar/Switch'
import { Icon } from '@/components/graphics/Icon'
import { cn } from '@/utilities/misc'
import { Spinner } from '@/components/graphics/Spinner'
import { formatDecimal } from '@/utilities/formatters'

export type FeedEditScheduleProps = {
  busyCulling: boolean
  value: FeedSchedule | undefined
  setValue: (value: FeedSchedule | undefined) => void
  disabled?: boolean
  /** Feed ingest time slots (HHMMSS), for server-side checks against the schedule */
  ingestSlots?: number[]
  photosCulled: number
  photosRemaining: number
}

//--------------------------------------------------------------------------------
export function FeedEditSchedule({
  busyCulling,
  value,
  setValue,
  disabled,
  ingestSlots,
  photosCulled,
  photosRemaining,
}: FeedEditScheduleProps) {
  const tenantId = useTenantId()
  const api = useApi({ requireLogin: true })

  const [validation, setValidation] = useState<{
    loading: boolean
    errors: string[]
    warnings: string[]
  }>({ loading: false, errors: [], warnings: [] })

  //--------------------------------------------------------------------------------
  const scheduleForApi = useMemo((): FeedSchedule => {
    const s: FeedSchedule = {
      cull: value?.cull?.length ? sortCulls(value.cull) : [],
    }
    if (value?.simulate === true) s.simulate = true
    return s
  }, [value])

  const ingestSlotsKey = ingestSlots?.join(',') ?? ''

  //--------------------------------------------------------------------------------
  useEffect(() => {
    if (!tenantId) {
      setValidation({ loading: false, errors: [], warnings: [] })
      return
    }

    let cancelled = false
    const timer = window.setTimeout(() => {
      ;(async () => {
        setValidation((prev) => ({ ...prev, loading: true }))
        const res = await api.request(feedValidateScheduleSpec, {
          path: { tenant: tenantId },
          body: {
            schedule: scheduleForApi,
            ...(ingestSlots?.length ? { ingestSlots } : {}),
          },
        })
        if (cancelled) return
        if (res.status === 200 && res.data) {
          setValidation({
            loading: false,
            errors: res.data.errors ?? [],
            warnings: res.data.warnings ?? [],
          })
        } else {
          setValidation({
            loading: false,
            errors: [res.error ?? 'Could not validate schedule'],
            warnings: [],
          })
        }
      })()
    }, VALIDATE_DEBOUNCE_MS)

    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [tenantId, api, scheduleForApi, ingestSlotsKey])

  //--------------------------------------------------------------------------------
  const culls = useMemo(
    () => sortCulls(value?.cull ?? []),
    [value?.cull]
  )

  //--------------------------------------------------------------------------------
  const updateAt = useCallback(
    (index: number, patch: Partial<FeedCull>) => {
      const next = [...culls]
      const cur = next[index]
      if (!cur) return
      let merged: FeedCull = { ...cur, ...patch }
      const hadWeek =
        cur.keepDayOfWeek != null && cur.keepDayOfWeek !== 0
      const hadMonth =
        cur.keepDayOfMonth != null && cur.keepDayOfMonth !== 0

      if (patch.keepDayOfMonth != null && patch.keepDayOfMonth !== 0) {
        merged = { ...merged, keepDayOfWeek: undefined }
        if (hadWeek) {
          toastWarning(
            "Can't keep photos from both a day of the month and a day of the week.",
          )
        }
      }
      if (patch.keepDayOfWeek != null && patch.keepDayOfWeek !== 0) {
        merged = { ...merged, keepDayOfMonth: undefined }
        if (hadMonth) {
          toastWarning(
            "Can't keep photos from both a day of the week and a day of the month.",
          )
        }
      }
      next[index] = merged
      commit(setValue, next, value?.simulate === true)
    },
    [culls, setValue, value?.simulate],
  )

  //--------------------------------------------------------------------------------
  const removeAt = useCallback(
    (index: number) => {
      commit(
        setValue,
        culls.filter((_, i) => i !== index),
        value?.simulate === true,
      )
    },
    [culls, setValue, value?.simulate],
  )

  //--------------------------------------------------------------------------------
  const swapAdjacentAt = useCallback(
    (index: number) => {
      const next = [...culls]
      const a = next[index]
      const b = next[index + 1]
      if (!a || !b || a.afterMonth !== b.afterMonth) return
      next[index] = b
      next[index + 1] = a
      commit(setValue, next, value?.simulate === true)
    },
    [culls, setValue, value?.simulate],
  )

  //--------------------------------------------------------------------------------
  const addRule = useCallback(() => {
    const maxAfter = culls.reduce((m, c) => Math.max(m, c.afterMonth), 0)
    const newRule: FeedCull = {
      afterMonth: culls.length === 0 ? 1 : maxAfter + 1,
    }
    commit(setValue, [...culls, newRule], value?.simulate === true)
  }, [culls, setValue, value?.simulate])

  //--------------------------------------------------------------------------------
  const setSimulate = useCallback(
    (next: SetStateAction<boolean>) => {
      commit(setValue, culls, typeof next === 'function'
        ? next(value?.simulate ?? false)
        : next
      )
    },
    [culls, setValue],
  )

  //--------------------------------------------------------------------------------
  return (
    <div className='FeedEditSchedule'>
      <p className='Intro'>
        Each rule applies after photos reach a given age. Later rules can
        further narrow what is kept in order to reduce long term costs.
        Rules are ordered from earlier to later.
      </p>

      {(validation.errors.length > 0 ||
        validation.warnings.length > 0
      ) && (
        <div className='ScheduleValidation'>
          {validation.errors.map((msg, i) => (
            <Notice
              key={`schedule-err-${i}`}
              variant='error'
              icon='exclamationTriangle'
            >
              {msg}
            </Notice>
          ))}
          {validation.warnings.map((msg, i) => (
            <Notice
              key={`schedule-warn-${i}`}
              variant='warning'
              icon='exclamationTriangle'
            >
              {msg}
            </Notice>
          ))}
        </div>
      )}

      {busyCulling && (
        <Notice variant='info'>
          <div className='FlexRow ItemsCenter Gap2'>
            <Spinner />
            Culling in progress
          </div>
        </Notice>
      )}

      {photosCulled >= 0 && (
        <Notice variant={photosRemaining > 0 ? 'info' : 'warning'}>
          {formatDecimal(photosCulled)} photos culled and {formatDecimal(photosRemaining)} remaining
          {photosCulled + photosRemaining > 0 && (<>
            {' '}
            ({formatDecimal(photosCulled / (photosCulled + photosRemaining) * 100, 1)}% reduction)
          </>)}

          {photosRemaining === 0 && (
            <p className='FlexRow ItemsCenter Gap2' style={{ fontSize: '1.5rem' }}>
              <Icon icon='exclamationTriangle' />
              All photos culled!
            </p>
          )}
        </Notice>
      )}

      <p className={cn(
        'SimulationSwitch',
        value?.simulate ? 'On' : 'Off'
      )}>
        <Switch
          label={`Simulate culling (${value?.simulate ? 'on' : 'off'})`}
          tooltip={<>
            <p>
              When switched on, culling runs in simulation mode. No photos are
              actually deleted or moved to cold storage. But when you use the
              player, you will see the remaining photos as though the others
              had been removed.
            </p>
            <p>
              We <b><i>strongly recommend</i></b> you switch this on to test
              before committing to actual culling.
            </p>
            <p>
              Note: The simulated removals are undone at billing time. Your 
              invoice will reflect the actual stored photos, not the ones hidden 
              by simulation.
            </p>
          </>}
          tooltipPlacement='bottom'
          value={value?.simulate}
          setValue={setSimulate}
        />
        {value?.simulate
          ? <Icon icon='check' />
          : <Icon icon='exclamationTriangle' />
        }
      </p>

      <div className='List'>
        {culls.map((rule, index) => (
          <Fragment key={`${rule.afterMonth}-${index}`}>
            <div className='CullRule'>

              <div className='CullRuleHeader'>
                <span className='CullRuleTitle'>Culling wave #{index + 1}</span>
                <IconButton
                  icon='trash'
                  tooltip='Remove this rule'
                  disabled={disabled}
                  onClick={() => removeAt(index)}
                />
              </div>
              <div className='Wrapped CullRuleBody'>

              <SelectBox<number>
                label='After'
                disabled={disabled}
                tooltip='When photos reach this age, this culling rule starts to apply.'
                options={afterMonthSelectOptions(rule.afterMonth)}
                valueToString={(v) => String(v)}
                valueFromString={(s) => parseInt(s, 10)}
                value={rule.afterMonth}
                setValue={(v) => {
                  const next = afterMonthChoiceFromSetState(v, rule.afterMonth)
                  const n = Number.isFinite(next) ? Math.max(0, Math.round(next)) : 0
                  updateAt(index, { afterMonth: n })
                }}
              />

              <Switch
                label={`Delete older photos (${rule.keepFromDay === FeedKeepFromDay.None ? 'on' : 'off'})`}
                disabled={disabled}
                value={rule.keepFromDay === FeedKeepFromDay.None}
                onCheckedChange={(checked) =>
                  updateAt(index, {
                    keepFromDay: checked
                      ? FeedKeepFromDay.None
                      : undefined,
                  })
                }
              />

              {rule.keepFromDay !== FeedKeepFromDay.None && (<>

                <SelectBox<RemoveHourChoice>
                  label='Remove before'
                  emptyOptionLabel='(none)'
                  disabled={disabled}
                  tooltip='Remove photos from before this hour of the day. Does not remove photos from that exact hour.'
                  options={REMOVE_HOUR_OPTIONS}
                  valueToString={(v) => (v === '' ? '' : String(v))}
                  valueFromString={(s) => {
                    if (s === '') return ''
                    const n = parseInt(s, 10)
                    return Number.isFinite(n) && n >= 0 && n <= 23 ? n : ''
                  }}
                  value={removeHourChoiceFromRule(rule.removeBeforeHour)}
                  setValue={(v) => {
                    const cur = removeHourChoiceFromRule(rule.removeBeforeHour)
                    const next = removeHourChoiceFromSetState(v, cur)
                    updateAt(index, {
                      removeBeforeHour: next === '' ? undefined : next,
                    })
                  }}
                />

                <SelectBox<RemoveHourChoice>
                  label='Remove after'
                  emptyOptionLabel='(none)'
                  disabled={disabled}
                  tooltip='Remove photos from after this hour of the day. Does not remove photos from that exact hour.'
                  options={REMOVE_HOUR_OPTIONS}
                  valueToString={(v) => (v === '' ? '' : String(v))}
                  valueFromString={(s) => {
                    if (s === '') return ''
                    const n = parseInt(s, 10)
                    return Number.isFinite(n) && n >= 0 && n <= 23 ? n : ''
                  }}
                  value={removeHourChoiceFromRule(rule.removeAfterHour)}
                  setValue={(v) => {
                    const cur = removeHourChoiceFromRule(rule.removeAfterHour)
                    const next = removeHourChoiceFromSetState(v, cur)
                    updateAt(index, {
                      removeAfterHour: next === '' ? undefined : next,
                    })
                  }}
                />

                <SelectBox<KeepDayOfWeekChoice>
                  label='Keep from week'
                  tooltip={`You can retain photos from every day of the week or limit to one to gain a big reduction.`}
                  disabled={disabled}
                  options={[
                    { label: '(all)', value: 0 },
                    { label: 'Sunday', value: 1 },
                    { label: 'Monday', value: 2 },
                    { label: 'Tuesday', value: 3 },
                    { label: 'Wednesday', value: 4 },
                    { label: 'Thursday', value: 5 },
                    { label: 'Friday', value: 6 },
                    { label: 'Saturday', value: 7 },
                  ]}
                  valueToString={(v) => String(v)}
                  valueFromString={(s) => parseInt(s, 10) as KeepDayOfWeekChoice}
                  value={
                    rule.keepDayOfWeek != null &&
                    rule.keepDayOfWeek >= 1 &&
                    rule.keepDayOfWeek <= 7
                      ? (rule.keepDayOfWeek as KeepDayOfWeekChoice)
                      : 0
                  }
                  setValue={(v) => {
                    const next = keepDayOfWeekChoiceFromSetState(
                      v,
                      rule.keepDayOfWeek,
                    )
                    updateAt(index, {
                      keepDayOfWeek: next === 0 ? undefined : next,
                    })
                  }}
                />

                <SelectBox<KeepDayOfMonthChoice>
                  label='Keep from month'
                  tooltip={`You can retain photos from every day of the month or limit to one to gain a big reduction.`}
                  disabled={disabled}
                  options={KEEP_DAY_OF_MONTH_OPTIONS}
                  valueToString={(v) => String(v)}
                  valueFromString={(s) => parseInt(s, 10)}
                  value={keepDayOfMonthToChoice(rule.keepDayOfMonth)}
                  setValue={(v) => {
                    const next = keepDayOfMonthChoiceFromSetState(
                      v,
                      rule.keepDayOfMonth,
                    )
                    updateAt(index, {
                      keepDayOfMonth: next === 0 ? undefined : next,
                    })
                  }}
                />

                <SelectBox<KeepFromDayChoice>
                  label='Keep from each day'
                  emptyOptionLabel='(all)'
                  disabled={disabled}
                  tooltip='(all) keeps every photo from each day. Otherwise choose what stays when this rule culls the rest.'
                  options={[
                    { label: 'First photo', value: FeedKeepFromDay.First },
                    { label: 'Middle photo', value: FeedKeepFromDay.Middle },
                    { label: 'Last photo', value: FeedKeepFromDay.Last },
                    { label: 'From time slots', value: FeedKeepFromDay.TimeSlots },
                  ]}
                  valueToString={(v) => (v === '' ? '' : String(v))}
                  valueFromString={(s) =>
                    s === '' ? '' : (parseInt(s, 10) as FeedKeepFromDay)
                  }
                  value={
                    rule.keepFromDay === undefined ? '' : rule.keepFromDay
                  }
                  setValue={(v) => {
                    const cur: KeepFromDayChoice =
                      rule.keepFromDay === undefined ? '' : rule.keepFromDay
                    const next = keepFromDayChoiceFromSetState(v, cur)
                    updateAt(index, {
                      keepFromDay: next === '' ? undefined : next,
                    })
                  }}
                />

                {rule.keepFromDay === FeedKeepFromDay.TimeSlots && (
                  <TimeSlots
                    label='Time slots'
                    tooltip='Be sure to align these with the time slots at time of ingestion'
                    value={timeSlotsToStr(rule.timeSlots)}
                    onChange={(s) =>
                      updateAt(index, { timeSlots: strToTimeSlots(s) })
                    }
                    disabled={disabled}
                    addFieldWidth='100%'
                  />
                )}

                <SelectBox<CulledPhotosFate>
                  label='Culled photos get'
                  disabled={disabled}
                  options={[
                    { label: 'Deleted', value: 'deleted' },
                    { label: 'Saved in cold storage', value: 'cold' },
                  ]}
                  valueToString={(v) => v}
                  valueFromString={(s) => s as CulledPhotosFate}
                  value={rule.coldStorage ? 'cold' : 'deleted'}
                  setValue={(v) => {
                    const next = culledPhotosFateFromSetState(v, !!rule.coldStorage)
                    updateAt(index, {
                      coldStorage: next === 'cold' ? true : undefined,
                    })
                  }}
                />

              </>)}

              </div>

            </div>

            {index < culls.length - 1 &&
              culls[index]?.afterMonth === culls[index + 1]?.afterMonth && (
                <div className='CullRuleSwap'>
                  <Button
                    label='Swap these two'
                    icon='hiMiniArrowsUpDown'
                    tooltip='Swap which of these rules gets applied sooner'
                    disabled={disabled || index >= culls.length - 1}
                    onClick={() => swapAdjacentAt(index)}
                  />
                </div>
              )
            }

          </Fragment>
        ))}
      </div>

      <div className='AddRow'>
        <Button
          label='Add culling rule'
          icon='plus'
          disabled={disabled}
          onClick={addRule}
        />
      </div>

    </div>
  )
}


//--------------------------------------------------------------------------------
function sortCulls(culls: FeedCull[]): FeedCull[] {
  return [...culls].sort((a, b) => a.afterMonth - b.afterMonth)
}

//--------------------------------------------------------------------------------
function commit(
  onChange: FeedEditScheduleProps['setValue'],
  culls: FeedCull[],
  simulate: boolean,
) {
  onChange((culls.length || simulate)
    ? {
      ...(simulate ? { simulate: true } : {}),
      cull: sortCulls(culls),
    }
    : undefined
  )
}

//--------------------------------------------------------------------------------
type KeepFromDayChoice = FeedKeepFromDay | ''

function keepFromDayChoiceFromSetState(
  v: SetStateAction<KeepFromDayChoice>,
  current: KeepFromDayChoice,
): KeepFromDayChoice {
  return typeof v === 'function' ? v(current) : v
}

type CulledPhotosFate = 'deleted' | 'cold'

function culledPhotosFateFromSetState(
  v: SetStateAction<CulledPhotosFate>,
  currentCold: boolean,
): CulledPhotosFate {
  const cur: CulledPhotosFate = currentCold ? 'cold' : 'deleted'
  return typeof v === 'function' ? v(cur) : v
}

//--------------------------------------------------------------------------------
/** 0 = all days (API omits field); 1–7 = Sunday–Saturday */
type KeepDayOfWeekChoice = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7

function keepDayOfWeekChoiceFromSetState(
  v: SetStateAction<KeepDayOfWeekChoice>,
  current: number | undefined,
): KeepDayOfWeekChoice {
  const cur: KeepDayOfWeekChoice =
    current != null && current >= 1 && current <= 7
      ? (current as KeepDayOfWeekChoice)
      : 0
  return typeof v === 'function' ? v(cur) : v
}

//--------------------------------------------------------------------------------
/** Ordinal for calendar days 1–28 (1st, 2nd, 3rd, …) */
function dayOfMonthOrdinal(n: number): string {
  const j = n % 10
  const k = n % 100
  if (k >= 11 && k <= 13) return `${n}th`
  if (j === 1) return `${n}st`
  if (j === 2) return `${n}nd`
  if (j === 3) return `${n}rd`
  return `${n}th`
}

//--------------------------------------------------------------------------------
/** 0 = all days; 1–28 forward; −1–−28 from month end (API `keepDayOfMonth`) */
type KeepDayOfMonthChoice = number

function keepDayOfMonthToChoice(d: number | undefined): KeepDayOfMonthChoice {
  if (d == null || d === 0) return 0
  if (d >= 1 && d <= 28) return d
  if (d <= -1 && d >= -28) return d
  return 0
}

//--------------------------------------------------------------------------------
function keepDayOfMonthChoiceFromSetState(
  v: SetStateAction<KeepDayOfMonthChoice>,
  current: number | undefined,
): KeepDayOfMonthChoice {
  const cur = keepDayOfMonthToChoice(current)
  return typeof v === 'function' ? v(cur) : v
}

//--------------------------------------------------------------------------------
/** Month counts for `afterMonth` (API stores months, not years). */
const AFTER_MONTH_OPTIONS: Array<SelectBoxOption<number> | '-----'> = [
  { label: 'First month', value: 1 },
  ...Array.from({ length: 22 }, (_, i) => ({
    label: i === 12
      ? 'Year 1'
      : `Month ${i + 2}`,
    value: i + 2,
  })),
  ...Array.from({ length: 9 }, (_, i) => {
    const y = i + 2
    return { label: `Year ${y}`, value: y * 12 }
  }),
  ...[20, 30, 40, 50, 60, 70, 80, 90, 100].map((y) => ({
    label: `Year ${y}`,
    value: y * 12,
  })),
]

//--------------------------------------------------------------------------------
const AFTER_MONTH_PREDEFINED = new Set(
  AFTER_MONTH_OPTIONS.flatMap((o) => (o === '-----' ? [] : [o.value])),
)

//--------------------------------------------------------------------------------
function afterMonthSelectOptions(current: number): Array<
  SelectBoxOption<number> | '-----'
> {
  if (Number.isFinite(current) && AFTER_MONTH_PREDEFINED.has(current))
    return AFTER_MONTH_OPTIONS
  return [
    { label: `${current} months`, value: current },
    '-----',
    ...AFTER_MONTH_OPTIONS,
  ]
}

//--------------------------------------------------------------------------------
function afterMonthChoiceFromSetState(
  v: SetStateAction<number>,
  current: number,
): number {
  return typeof v === 'function' ? v(current) : v
}

//--------------------------------------------------------------------------------
const KEEP_DAY_OF_MONTH_OPTIONS: Array<SelectBoxOption<number> | '-----'> = [
  { label: '(all)', value: 0 },
  '-----',
  { label: 'First day', value: 1 },
  ...Array.from({ length: 27 }, (_, i) => {
    const d = i + 2
    return { label: dayOfMonthOrdinal(d), value: d }
  }),
  '-----',
  ...Array.from({ length: 14 }, (_, i) => ({
    label:
      i === 0
        ? 'Last day'
        : i === 27
          ? 'Last - 28'
          : `Last - ${i}`,
    value: -(i + 1),
  })),
]

const VALIDATE_DEBOUNCE_MS = 400

//--------------------------------------------------------------------------------
function removeHourLabel(h: number): string {
  if (h === 12) return 'Noon'
  if (h >= 1 && h <= 11) return `${h} am`
  return `${h - 12} pm`
}

//--------------------------------------------------------------------------------
const REMOVE_HOUR_OPTIONS: SelectBoxOption<number>[] = Array.from(
  { length: 23 },
  (_, h) => ({
    label: removeHourLabel(h + 1),
    value: h + 1,
  }),
)

//--------------------------------------------------------------------------------
type RemoveHourChoice = number | ''

function removeHourChoiceFromRule(h: number | undefined): RemoveHourChoice {
  if (h === undefined) return ''
  if (!Number.isInteger(h) || h < 0 || h > 23) return ''
  return h
}

//--------------------------------------------------------------------------------
function removeHourChoiceFromSetState(
  v: SetStateAction<RemoveHourChoice>,
  current: RemoveHourChoice,
): RemoveHourChoice {
  return typeof v === 'function' ? v(current) : v
}
